import { Col, Collapse, Row, Typography } from "antd";
import type { CollapseProps } from "antd";
import CollapseExpandIcon from "../CollapseExpandIcon";
import SwitchOptionComponent from "../SwitchComponent";
import InputNumberComponent from "../InputNumberComponent";
import InputComponent from "../InputComponent";
import RichTextComponent from "../RichTextComponent";
import { Culture } from "../../context/VolunteersHighlightContext/constants";
import { VolunteersFAQRules } from "../../context/VolunteersFAQContext/constants";
import {
	ANSWER_EDITOR_HEIGHT,
	GENERAL_SECTION_KEY,
	getQuestionSectionKey,
	VolunteersFAQFormKeys,
	VolunteersFAQGeneralTexts,
	VolunteersFAQQuestionTexts,
	VolunteersFAQSectionLabels,
} from "./constants";

export interface VolunteersFAQGeneralValues {
	isActive: boolean;
	displayOrder?: number;
}

export interface VolunteersFAQLocalizableValues {
	culture: Culture;
	question: string;
	answer: string;
}

export type VolunteersFAQLocalizableChanges = Partial<Pick<VolunteersFAQLocalizableValues, "question" | "answer">>;

export const applyFAQLocalizableChanges = <T extends { culture: Culture }>(
	localizables: T[],
	culture: Culture,
	changes: VolunteersFAQLocalizableChanges
): T[] => localizables.map(item => (item.culture === culture ? { ...item, ...changes } : item));

interface VolunteersFAQFormProps {
	general: VolunteersFAQGeneralValues;
	localizables: VolunteersFAQLocalizableValues[];
	// On the add form both texts are required; the update form only sends what changed.
	isRequired: boolean;
	onGeneralChange: (changes: Partial<VolunteersFAQGeneralValues>) => void;
	onLocalizableChange: (culture: Culture, changes: VolunteersFAQLocalizableChanges) => void;
}

// "Genel Bilgiler" with the status and the order, then one "Merak Edilenler - <language>" section per culture with its
// question and its rich text answer.
const VolunteersFAQForm = ({ general, localizables, isRequired, onGeneralChange, onLocalizableChange }: VolunteersFAQFormProps) => {
	const items: CollapseProps["items"] = [
		{
			key: GENERAL_SECTION_KEY,
			label: VolunteersFAQSectionLabels.GENERAL,
			children: (
				<>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={12} md={8} lg={6}>
							<SwitchOptionComponent
								title={VolunteersFAQGeneralTexts.isActive.label}
								helperText={VolunteersFAQGeneralTexts.isActive.helperText}
								value={general.isActive}
								onChange={checked => onGeneralChange({ isActive: checked })}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={12} md={6} lg={4}>
							<InputNumberComponent
								title={VolunteersFAQGeneralTexts.displayOrder.label}
								positiveOnly
								value={general.displayOrder}
								placeholder={VolunteersFAQGeneralTexts.displayOrder.placeholder}
								onChange={value => onGeneralChange({ displayOrder: value === null || value === "" ? undefined : Number(value) })}
							/>
						</Col>
					</Row>
				</>
			),
		},
		...localizables.map(localizable => {
			const texts = VolunteersFAQQuestionTexts[localizable.culture];

			return {
				key: getQuestionSectionKey(localizable.culture),
				label: VolunteersFAQSectionLabels.getQuestion(localizable.culture),
				children: (
					<Row gutter={[20, 16]}>
						<Col span={24}>
							<InputComponent
								title={texts.question.label}
								isRequired={isRequired}
								placeholder={texts.question.placeholder}
								value={localizable.question}
								maxLength={VolunteersFAQRules.questionMaxLength}
								onChange={e => onLocalizableChange(localizable.culture, { question: e.target.value })}
							/>
						</Col>
						<Col span={24}>
							<Typography.Title level={5} style={{ color: "#2e333e" }}>
								{texts.answer.label}
								{isRequired && <span style={{ color: "red" }}>&nbsp;*</span>}
							</Typography.Title>
							<RichTextComponent
								height={ANSWER_EDITOR_HEIGHT}
								resizable={false}
								messageTemplate={localizable.answer}
								backgroundColor="transparent"
								placeholder={texts.answer.placeholder}
								handleSetChangedTemplate={answer => onLocalizableChange(localizable.culture, { answer })}
								maxLength={VolunteersFAQRules.answerMaxLength}
							/>
						</Col>
					</Row>
				),
			};
		}),
	];

	return (
		<Collapse
			items={items}
			expandIconPosition="end"
			bordered={false}
			ghost
			expandIcon={CollapseExpandIcon}
			defaultActiveKey={VolunteersFAQFormKeys}
		/>
	);
};

export default VolunteersFAQForm;
